"use client";

import { useEffect, useRef, Suspense } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { trackPageView } from "@/lib/analytics";

const FB_PIXEL_ID = "643462085426001";
const CLARITY_ID = "slui46asl3";

const resolveGaId = () => {
  if (process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID) {
    return process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  }
  const appFor = (process.env.NEXT_PUBLIC_APPLICATION_FOR || "").toLowerCase();
  if (appFor === "production_synnnc" || appFor === "synnnc") {
    return "G-7YTLWHPFFW";
  }
  return "G-WGYWW9KLCD";
};

const GA_MEASUREMENT_ID = resolveGaId();

// Same semantics as before: on the server there is no window, so "not
// localhost" holds and this is true — server and client must agree or the
// component would hydrate with a mismatch.
const isAnalyticsEnabled = () => {
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");
  return !isLocalhost || process.env.NEXT_PUBLIC_FORCE_ANALYTICS === "true";
};

// ---------------------------------------------------------------------------
// Queue stubs — installed immediately, cost nothing
// ---------------------------------------------------------------------------
//
// Meta Pixel (fbevents.js, ~200 KB), gtag.js (~177 KB) and Clarity drove
// most of the main-thread blocking time on every page, and loaded on every
// route shortly after the load event. The expensive part is the library, not
// the API surface: each vendor's documented snippet is a tiny function that
// queues calls until its library arrives and replays them in order.
//
// So the stubs go in first and the libraries later. Nothing is dropped: an
// event fired before the library loads queues and is sent when it does.
//
// This also closes two gaps in the previous setup, both measured on an event
// page. fbq was only defined once the lazyOnload snippet ran, after the page
// had already fired its view event, and lib/metaPixel.js skips an event
// entirely when fbq doesn't exist — so Meta events fired on mount were lost.
// And gtag's `config` was only queued by that same late snippet, so GA4's
// view_item, pushed before it, went unsent while page_view got through.
//
// Installed at module evaluation, which happens before hydration and so
// before any component effect can fire an event.
function installQueueStubs() {
  const w = window;
  if (w.__tykkitAnalyticsStubs) return;
  w.__tykkitAnalyticsStubs = true;

  // Meta Pixel — the official snippet's queue, without its <script> insertion.
  if (!w.fbq) {
    const n = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    w.fbq = n;
    if (!w._fbq) w._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
  }
  // init must precede any track call in the queue.
  w.fbq("init", FB_PIXEL_ID);
  w.fbq("track", "PageView");

  // GA4 — gtag() queues into dataLayer until gtag.js loads. `config` goes in
  // first so every later event has a destination when the queue replays.
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== "function") {
    w.gtag = function () {
      w.dataLayer.push(arguments);
    };
  }
  w.gtag("js", new Date());
  w.gtag("config", GA_MEASUREMENT_ID, {
    transport_type: "beacon",
    send_page_view: false,
  });

  // Clarity — its own documented queue.
  w.clarity =
    w.clarity ||
    function () {
      (w.clarity.q = w.clarity.q || []).push(arguments);
    };
}

if (typeof window !== "undefined" && isAnalyticsEnabled()) installQueueStubs();

// ---------------------------------------------------------------------------
// Library loading — deferred off the critical path
// ---------------------------------------------------------------------------
//
// Loaded on the first sign of engagement, or after the page has settled for
// a few seconds, whichever comes first. Either way the injection itself
// happens in an idle callback, never inside an input handler, so it doesn't
// lengthen the interaction that triggered it.
//
// Every route still loads all three — conversion attribution across the
// whole journey is unchanged. Restricting the Pixel to checkout routes would
// remove more main-thread work, but changes what Meta can attribute, so it's
// a marketing decision rather than a code one.
// Input events are listened for in the capture phase so nothing can stop
// them first. `scroll` is the exception: it doesn't bubble, so a capturing
// listener on window also receives scrolls *inside* elements — and the
// homepage's auto-advancing carousels fire those continuously from ~300ms
// with no user present, which loaded everything immediately. A non-capture
// window listener hears only scrolling of the page itself.
const INPUT_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"];
const SETTLE_DELAY_MS = 4000;

const LIBRARY_URLS = () => [
  "https://connect.facebook.net/en_US/fbevents.js",
  `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`,
  `https://www.clarity.ms/tag/${CLARITY_ID}`,
];

const whenIdle = (fn) =>
  typeof window.requestIdleCallback === "function"
    ? window.requestIdleCallback(fn, { timeout: 2000 })
    : window.setTimeout(fn, 1);

function injectLibraries() {
  const w = window;
  if (w.__tykkitAnalyticsLoaded) return;
  w.__tykkitAnalyticsLoaded = true;

  for (const src of LIBRARY_URLS()) {
    const script = document.createElement("script");
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  }
}

function useDeferredLibraries(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;

    let settleTimer;
    const cleanups = [];

    const load = () => {
      cleanups.forEach((fn) => fn());
      window.clearTimeout(settleTimer);
      whenIdle(injectLibraries);
    };

    const inputOpts = { once: true, passive: true, capture: true };
    for (const type of INPUT_EVENTS) {
      window.addEventListener(type, load, inputOpts);
      cleanups.push(() => window.removeEventListener(type, load, inputOpts));
    }
    const scrollOpts = { once: true, passive: true };
    window.addEventListener("scroll", load, scrollOpts);
    cleanups.push(() => window.removeEventListener("scroll", load, scrollOpts));

    const startSettleTimer = () => {
      settleTimer = window.setTimeout(load, SETTLE_DELAY_MS);
    };
    if (document.readyState === "complete") {
      startSettleTimer();
    } else {
      window.addEventListener("load", startSettleTimer, { once: true });
      cleanups.push(() =>
        window.removeEventListener("load", startSettleTimer),
      );
    }

    return () => {
      cleanups.forEach((fn) => fn());
      window.clearTimeout(settleTimer);
    };
  }, [enabled]);
}

function AnalyticsTracker({ analyticsEnabled }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialRouteRef = useRef(true);
  const lastTrackedPath = useRef(null);

  // Track SPA PageViews on client-side route changes
  useEffect(() => {
    if (!pathname) return;
    // Do not track /redirect/* as standard landing pageviews
    if (pathname.startsWith("/redirect")) return;

    const url =
      pathname +
      (searchParams?.toString() ? `?${searchParams.toString()}` : "");

    if (lastTrackedPath.current === url) return;
    lastTrackedPath.current = url;

    // GA4 Page View Tracking
    trackPageView({
      url,
      title: typeof document !== "undefined" ? document.title : "",
    });

    // The initial PageView is queued by installQueueStubs().
    if (initialRouteRef.current) {
      initialRouteRef.current = false;
      return;
    }

    // Meta Pixel SPA PageView Tracking
    if (analyticsEnabled && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [pathname, searchParams, analyticsEnabled]);

  return null;
}

export default function ProductionAnalytics() {
  const analyticsEnabled = isAnalyticsEnabled();
  useDeferredLibraries(analyticsEnabled);

  if (!analyticsEnabled) return null;

  return (
    <>
      <Suspense fallback={null}>
        <AnalyticsTracker analyticsEnabled={analyticsEnabled} />
      </Suspense>

      {/* Meta Pixel no-JS fallback — unaffected by the deferral. */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src={`https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
    </>
  );
}
